import React from 'react';
import FieldTextValue from './FieldTextValue.jsx';
import { Modal } from '../ui-components/Modal.jsx';
import ExpandableBio from '../ui-components/ExpandableBio.jsx';
import SpeakerHeadshot from '../ui-components/SpeakerHeadshot.jsx';
import { Clock, MapPin, GraduationCap } from 'lucide-react';
import { getSessionSpeakerDisplayName, hasValidKey, isDisplayableRange } from '../utils/helpers.js';
import { FIXED_SESSION_FIELD_IDS } from '../constants.js';
import { useI18n } from '../i18n/index.jsx';
import SpeakerDisclosure from './SpeakerDisclosure.jsx';
import { groupSpeakersBySlug, roleGroupHeading } from '../utils/group-speakers-by-role.js';
import ceuCreditsLabel from '../utils/ceu-credits-label.js';

/**
 * SB-8856 — subsession detail modal. Pushed onto the widget's modal stack from
 * a subsession row in SessionModal (or a schedule sub-session card); Back, ×,
 * and the overlay all pop ONE level, matching the existing stack behavior.
 *
 * The payload subsession is already field-gated by the API (same projection
 * as parent sessions), so this renders the same field set the organizer
 * selected for the embed: time, location, CEU, speakers (grouped by role
 * slug with drill-through), description, and custom fields.
 */
const SubsessionModal = ({
  visibleSpeakerFields = [],
  subsessionData,
  visibleSessionFields = [],
  visibleSessionFieldsById = {},
  onClose,
  onBack,
  onSpeakerClick,
  primaryColor,
}) => {
  const { t } = useI18n();

  if (!subsessionData) return null;

  const getFieldValue = fieldId => subsessionData[fieldId] || '-';
  const getFieldListValue = fieldId => {
    const list = subsessionData[`${fieldId}List`];
    return Array.isArray(list) ? list.filter(Boolean) : null;
  };
  const formatFieldLabel = fieldId =>
    visibleSessionFieldsById[fieldId]?.name || fieldId.charAt(0).toUpperCase() + fieldId.slice(1) + ':';

  const customVisibleFields = visibleSessionFields.filter(fieldId => !FIXED_SESSION_FIELD_IDS.includes(fieldId));
  const speakerGroups = groupSpeakersBySlug(subsessionData.speakers);

  return (
    <Modal
      onClose={onClose}
      onBack={onBack}
      className="session-modal subsession-modal"
      contentClassName="session-modal__content"
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      <div className="session-modal__session-header">
        <div className="session-modal__session-info">
          <h1 className="session-modal__title" style={{ color: primaryColor }}>
            {subsessionData.title}
          </h1>

          <div className="session-modal__meta">
            {isDisplayableRange(subsessionData?.date) &&
            (visibleSessionFields.includes('starts_at') || visibleSessionFields.includes('ends_at')) ? (
              <div className="session-modal__date">
                <Clock className="session-modal__icon" style={{ color: primaryColor }} />
                <span>{subsessionData.date}</span>
              </div>
            ) : null}
            {hasValidKey(subsessionData, 'location') && visibleSessionFields.includes('location') ? (
              <div className="session-modal__location">
                <MapPin className="session-modal__icon" style={{ color: primaryColor }} />
                <span>{subsessionData.location}</span>
              </div>
            ) : null}
            {hasValidKey(subsessionData, 'ceuCredits') && visibleSessionFields.includes('ceu_credits') ? (
              <div className="session-modal__location">
                <GraduationCap className="session-modal__icon" style={{ color: primaryColor }} />
                <span>
                  {ceuCreditsLabel([visibleSessionFieldsById], t)} {subsessionData.ceuCredits}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Speakers — same slug grouping + drill-through as the session modal */}
      {visibleSessionFields.includes('speakers') && subsessionData.speakers?.length > 0 && (
        <div className="session-modal__speakers">
          {speakerGroups.map(group => (
            <div key={group.slug} className="session-modal__speaker-group">
              <h2 className="session-modal__speakers-title">{roleGroupHeading(t, group)}</h2>
              <div className="session-modal__speakers-grid">
                {group.speakers.map(speaker => (
                  <div
                    key={`${speaker.id}-${group.slug}`}
                    className="session-modal__speaker-card"
                    onClick={() => onSpeakerClick?.(speaker.id)}
                  >
                    <SpeakerHeadshot speaker={speaker} />
                    <div className="session-modal__speaker-info">
                      <h3 className="session-modal__speaker-name">{getSessionSpeakerDisplayName(speaker, visibleSpeakerFields)}</h3>
                      {speaker.title && <p className="session-modal__speaker-title">{speaker.title}</p>}
                      {speaker.company && <p className="session-modal__speaker-company">{speaker.company}</p>}
                      <SpeakerDisclosure disclosure={speaker.disclosure} compact />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="session-modal__tab-content">
        <div className="session-modal__details">
          {visibleSessionFields.includes('description') && subsessionData.description ? (
            <div className="session-modal__description">
              <ExpandableBio
                htmlContent={subsessionData.description}
                maxLength={300}
                className="session-modal__description-content"
              />
            </div>
          ) : null}

          {customVisibleFields.length > 0 && (
            <div className="session-modal__session-details">
              {customVisibleFields
                .filter(fieldId => {
                  const value = subsessionData[fieldId];
                  return value !== null && value !== undefined && value !== '';
                })
                .map(fieldId => {
                  const listValue = getFieldListValue(fieldId);
                  return (
                    <div key={fieldId} className="session-modal__detail-item">
                      <span className="session-modal__detail-label">{formatFieldLabel(fieldId)}</span>
                      {listValue ? (
                        <span className="session-modal__detail-value sb-dot-list">
                          {listValue.map((value, i) => (
                            <span key={i} className="sb-dot-list__item">
                              {value}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <FieldTextValue html={subsessionData[`${fieldId}Html`]} className="session-modal__detail-value">
                          {getFieldValue(fieldId)}
                        </FieldTextValue>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SubsessionModal;
